import type { RecursiveReadonly } from "@p0security/util/types/util";

import type { Maturity } from "../../integrations/shared.ts";
import type { Delegate } from "./delegate.ts";
import type { Output } from "./output.ts";
import type { Parameter } from "./parameter.ts";
import type { StagedRequestOf } from "./request.ts";

export type Access<
  Generated extends Record<never, Output>,
  Parameters extends Record<never, Parameter>,
  Delegates extends Record<string, Delegate<any, any>>,
> = {
  /** Access delegation
   *
   * Additional systems in which access must be provisioned as a prerequisite, keyed by Delegate string identifier
   */
  readonly delegation: Delegates;
  /** Long-form description of an access parameter
   *
   * Will be displayed in small text or placeholder in notifiers
   */
  readonly description: string;
  /** Generated access outputs
   *
   * These are generated during the "stage" access lifecycle phase.
   * Only add outputs here under the following circumstances:
   *
   * 1. The content should only be determined after approval.
   * 2. Or the generation would take too long for a human to wait.
   *
   * Otherwise use parameters of type generated.
   */
  readonly generated: Generated;
  /** Short human-friendly description of an access parameter
   *
   * Displayed as the access option label in notifiers
   */
  readonly label: string;
  /** Access-definition parameters
   *
   * These configure what is being accessed, and with which entitlements
   */
  readonly parameters: Parameters;
  /** Defines how requests for this access are rendered */
  readonly render: {
    /** The instance in which access is provisioned */
    readonly locator?: keyof Parameters & string;
    /** The parameter that holds the policy for this access */
    readonly policy?: keyof Parameters & string;
    /** The parameter that holds the resource for this access */
    readonly resource?: keyof Parameters & string;
    /** The instructions to inform the user of any steps or info needed to use the granted access */
    readonly instructions?: (
      request: StagedRequestOf<Access<Generated, Parameters, Delegates>>
    ) => string | undefined;
  };
  /** Maturity level of this access type
   *
   * Controls the visibility of feature maturity badges in the UI. When set to "beta" or "preview",
   * a blue "Beta" tag is displayed next to the access option label in the access request modal.
   * When set to "ga" (generally available), no badge is shown.
   *
   * @see {@link Maturity} for available values
   */
  readonly maturity: Maturity;
  /** A parameter the target system holds one value of per resource, for one person
   *
   * For example, GitHub gives a person one role per repository. Two Grants of one Request
   * that ask for different values on the same resource can't both be granted, so P0
   * refuses them when a bundle is saved and when a Request is created.
   */
  readonly singleValued?: {
    // Plain strings: `keyof Parameters` here would make types that infer an access's
    // parameters (`RequestOf` and the like) infer them from these names too
    /** The parameter that takes one value */
    readonly parameter: string;
    /** The parameter naming the resource that takes one value of it */
    readonly per: string;
  };
};

/** Use to define an access literal */
export const Access = <
  // Need `Record<string, ...>` in order to provide user type feedback
  A extends Access<
    Record<string, Output>,
    Record<string, Parameter>,
    Record<string, Delegate<any, any>>
  >,
>(
  access: A
) => access as RecursiveReadonly<A>;
